/* eslint-disable */
import { PlatformLocation } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { SplashScreen } from '@capacitor/splash-screen';
import { AlertController, IonRouterOutlet, ModalController, Platform } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs/operators';
import { CONSTANTS } from 'src/app/services/config.service';
import { environment } from 'src/environments/environment';
import { SafeAreaBarStyle, SafeAreaConfig } from './adapters/safe-area/safe-area.config';
import { LanguageDirection } from './adapters/translation/model/language-direction.model';
import { MSRemoteLoggerCategory } from './adapters/remote-logger/utils/remote-logger.utils';
import { observableAsPromise } from './pages/utils/promise.utils';
import { LoggerService } from './services/logger.service';
import { PubSubService } from './services/pub-sub.service';
import { UrlService } from './services/url.service';
import { WebViewService } from './services/webview-background/webview.service';
import { RemoteLoggerUseCase } from './use-case/remote-logger/remote-logger.use-case';
import { SafeAreaUseCase } from './use-case/safe-area/safe-area.use-case';
import { TextZoomUseCase } from './use-case/text-zoom/text-zoom.use-case';
import { TranslationUseCase } from './use-case/translation/translation.use-case';
import { ThemeWrapper } from './wrappers/theme.wrapper';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss']
})
export class AppComponent implements OnInit {
  @ViewChild(IonRouterOutlet, { static: true }) routerOutlet: IonRouterOutlet;
  dark = true;

  navigationTrigger: string = null;
  isBrowserMove: boolean;
  languageDirection: string = LanguageDirection.LEFT_TO_RIGHT;
  appLanguage: string = "";

  constructor(
    private platform: Platform,
    private router: Router,
    private alertCtrl: AlertController,
    private modalCtrl: ModalController,
    private logger: LoggerService,
    private pltLocation: PlatformLocation,
    private url: UrlService,
    private translate: TranslateService,
    private translationUseCase: TranslationUseCase,
    private safeAreaUseCase: SafeAreaUseCase,
    private textZoomUseCase: TextZoomUseCase,
    // private navigationBarService: NavigationBarService,
    private webViewService: WebViewService,
    private theme: ThemeWrapper,
    private pubSub: PubSubService,
    private remoteLogger: RemoteLoggerUseCase
  ) {
    this.initializeApp();
  }

  initializeApp() {
    this.remoteLogger.setUserProperty({ ip_address: '{{auto}}' });
    this.logger.log('App.component - in initializeApp', MSRemoteLoggerCategory.INIT);

    const appInfo = {
      VERSION: environment.browserAppConfig.version,
      PACKAGE_NAME: environment.browserAppConfig.appId
    };

    this.platform.ready().then(async () => {
      this.logger.log('App.component - in initializeApp - platform ready', MSRemoteLoggerCategory.INIT);
      this.logger.log('App.component - platforms', MSRemoteLoggerCategory.INIT, this.platform.platforms());
      this.logger.log('App.component - server', MSRemoteLoggerCategory.INIT, environment.serverUrl);
      this.logger.log('App.component - production', MSRemoteLoggerCategory.INIT, environment.production);
      this.logger.log('App.component - constants - USE_SERVER_DB', MSRemoteLoggerCategory.INIT, CONSTANTS.USE_SERVER_DB);
      this.logger.log('App.component - constants - APP', MSRemoteLoggerCategory.INIT, appInfo);
      this.logger.log('App.component - constants - IS_DEVICE', MSRemoteLoggerCategory.INIT, CONSTANTS.IS_DEVICE);
      this.logger.log('App.component - constants - IS_BROWSERMOVE', MSRemoteLoggerCategory.INIT, CONSTANTS.IS_BROWSERMOVE);

      if (CONSTANTS.IS_DEVICE) {
        this.backButtonEvent();
        this.logger.log('Calling splash screen hide', MSRemoteLoggerCategory.INIT);

        const safeAreaConfig: SafeAreaConfig = {
          navigationBarColor: this.theme.ionColorBackground,
          statusBarColor: this.theme.ionColorBackground,
          barStyle: SafeAreaBarStyle.Light
        };

        const safeAreaObs = this.safeAreaUseCase.enable(safeAreaConfig);
        await observableAsPromise(safeAreaObs);

        await this.webViewService.setBackgroundColor(this.theme.ionColorBackground);

        SplashScreen.hide();
      } else {
        this.onPop();
      }
    });
  }

  onPop() {
    this.pltLocation.onPopState(async () => {
      const dontWarnPages = ['#/call-center'];
      if (dontWarnPages.includes(this.pltLocation.hash)) {
        return;
      }

      const modal = await this.modalCtrl.getTop();
      if (modal) {
        modal.dismiss();
      } else {
        this.warnNoBackButton();
      }
    });
  }

  backButtonEvent() {
    //Note: This disables the back button, aside for use on the dashboard
    //We'll enable use of the back button when navigation is working properly
    this.platform.backButton.subscribeWithPriority(10, async () => {
      if (this.router.url === '/dashboard') {
        navigator['app'].exitApp();
      }
    });
  }

  async warnNoBackButton() {
    const alert = await this.alertCtrl.create({
      message: this.translate.instant('APP_WEB_DONT_CLICK_BACK'),
      buttons: [
        {
          text: this.translate.instant('BUTTON_OK'),
          role: 'ok'
        }
      ]
    });

    await alert.present();
  }

  ngOnInit() {
    this.router.events.pipe(filter((event) => event instanceof NavigationStart)).subscribe((event: NavigationStart) => {
      this.navigationTrigger = event.navigationTrigger;
      this.url.setNavigationTrigger(this.navigationTrigger);
      // alert(this.navigationTrigger)
    });

    this.pubSub.subscribe(CONSTANTS.EVENTS.browserMoveFlagSet, (...args) => {
      this.logger.log('Setting bMove flag in app component', MSRemoteLoggerCategory.INIT);
      this.isBrowserMove = CONSTANTS.IS_BROWSERMOVE;
    });

    this.translationUseCase.getLanguageChangeStream().subscribe((languageChangeEvent) => {
      this.appLanguage = languageChangeEvent.language;
      this.languageDirection = languageChangeEvent.direction;
    });

    this.textZoomUseCase.monitorTextZoomChanges().subscribe();
  }
}
